import { Controller } from "@hotwired/stimulus";
import { renderAssistantMarkdown } from "../lib/assistant_markdown";

// In-app Savant copilot panel. Holds the conversation client-side and posts the
// running history to /ask_savant; renders the assistant's reply. The server
// scopes reads/actions to the signed-in user's Savant permissions and never
// sends anything external.
export default class extends Controller {
  static targets = ["panel", "messages", "input", "prompts", "jump"];

  // How long one turn is polled before the panel stops waiting. The layout
  // passes AgentTask::ConsoleCopilotRecovery::PANEL_WAIT_SECONDS, which
  // outlasts the recovery that fails a turn whose worker died, so the person
  // reads what happened instead of "still working".
  static values = { maxPollSeconds: { type: Number, default: 720 } };

  static POLL_INTERVAL_MS = 2000;

  connect() {
    // Rehydrate conversation from sessionStorage so the copilot panel
    // survives Turbo navigations.  Keyed per signed-in user (not path) so
    // history follows the user across pages without leaking to whoever
    // else uses the browser/session, and capped to avoid unbounded growth.
    const userId = document.querySelector('meta[name="current-user-id"]')?.content;
    this.storageKey = userId ? `ask_savant_history:${userId}` : null;
    this.history = [];
    this.busy = false;
    this.followLatest = true;

    if (!this.storageKey) return;

    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.history = parsed.slice(-50);
          this.#rehydrateMessages();
        }
      }
    } catch (_e) {
      // sessionStorage might be unavailable (private mode, quota); degrade
      // gracefully to a fresh conversation.
    }
  }

  open(event) {
    event?.preventDefault();
    this.panelTarget.classList.remove("hidden");
    requestAnimationFrame(() => {
      this.panelTarget.classList.add("ask-open");
      this.inputTarget?.focus();
    });
  }

  close(event) {
    event?.preventDefault();
    this.panelTarget.classList.remove("ask-open");
    setTimeout(() => this.panelTarget.classList.add("hidden"), 200);
  }

  toggle(event) {
    event?.preventDefault();
    if (this.panelTarget.classList.contains("hidden")) this.open();
    else this.close();
  }

  key(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  submit(event) {
    event?.preventDefault();
    this.send();
  }

  // A suggested-prompt chip. It puts its own text in the input and then hands
  // off to send(), so a chip and a typed question travel exactly the same path
  // — there is no second submit to keep in step. The drawer opens first because
  // the answer appears there, not where the chip was.
  usePrompt(event) {
    event?.preventDefault();
    const prompt = event?.currentTarget?.dataset?.prompt;
    if (!prompt || this.busy) return;

    if (this.panelTarget.classList.contains("hidden")) this.open();
    this.inputTarget.value = prompt;
    this.send();
  }

  // Chips are the way in, so they step aside once a conversation is under way.
  hidePrompts() {
    if (!this.hasPromptsTarget) return;
    this.promptsTargets.forEach((row) => row.classList.add("hidden"));
  }

  async send() {
    const text = this.inputTarget.value.trim();
    if (!text || this.busy) return;

    this.inputTarget.value = "";
    // Sending a question means following the conversation again: the reader
    // sees their own message and the answer that follows it.
    this.followLatest = true;
    this.jumpTarget.classList.add("hidden");
    this.appendMessage("user", text);
    this.hidePrompts();
    this.history.push({ role: "user", content: text });
    this.#persistHistory();
    this.setBusy(true);

    try {
      const res = await fetch("/ask_savant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ messages: this.history, context: this.pageContext() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.task_id) {
        const answer = (data && data.error) || "Savant could not answer that.";
        this.appendMessage("assistant", answer, { plain: true });
        this.history.push({ role: "assistant", content: answer });
        this.#persistHistory();
        return;
      }
      const outcome = await this.awaitTurn(data.task_id);
      const answer =
        outcome.text ||
        outcome.error ||
        (outcome.timedOut
          ? "Savant is still working on this one — it has been running a long time. Check the Tasks board for its result."
          : "Savant could not answer that.");
      this.appendMessage("assistant", answer, { plain: !outcome.text });
      this.history.push({ role: "assistant", content: answer });
      this.#persistHistory();
    } catch (_e) {
      this.appendMessage("assistant", "Savant could not respond just now. Please try again.", { plain: true });
    } finally {
      this.setBusy(false);
    }
  }

  // Polls the status endpoint until the turn reaches a terminal state. The
  // thinking bubble always shows where things stand ("queued" vs live tool
  // counts) so a slow turn never reads as a silent spinner.
  async awaitTurn(taskId) {
    let delay = 0;
    let consecutiveFailures = 0;
    let elapsedSeconds = 0;
    for (;;) {
      if (elapsedSeconds >= this.maxPollSecondsValue) return { timedOut: true };
      await new Promise((resolve) => setTimeout(resolve, delay));
      elapsedSeconds += Math.max(delay / 1000, 1);
      delay = this.constructor.POLL_INTERVAL_MS;

      let data;
      try {
        const res = await fetch(`/ask_savant/statuses/${taskId}`, { headers: { Accept: "application/json" } });
        if (!res.ok) return {};
        data = await res.json();
        consecutiveFailures = 0;
      } catch (_e) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= 5) return { error: "Lost contact with Savant mid-turn. Please try again." };
        continue;
      }

      switch (data.status) {
        case "succeeded":
          return { text: data.text };
        case "failed":
        case "cancelled":
        case "suppressed":
          return { error: data.error || "Savant could not answer that." };
        case "running": {
          const tools = data.tools_used || 0;
          this.updateThinking(
            tools > 0 ? `Kerri is working — ${tools} tools used so far` : "Kerri is reading your data…"
          );
          break;
        }
        default:
          this.updateThinking("Kerri is getting started…");
      }
    }
  }

  appendMessage(role, text, { plain = false } = {}) {
    const row = document.createElement("div");
    row.className = role === "user" ? "flex justify-end" : "flex justify-start";
    row.dataset.role = role;
    const bubble = document.createElement("div");
    bubble.className =
      role === "user"
        ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm border border-accent-500/30 bg-accent-500/15 px-3 py-2 text-fg"
        : "max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-line bg-ink-800 px-3 py-2 text-fg";

    if (role === "assistant" && !plain) {
      // Rendered Markdown carries its own block spacing; pre-wrap would double
      // every newline between blocks.
      bubble.classList.remove("whitespace-pre-wrap");
      bubble.classList.add("assistant-md");
      bubble.innerHTML = renderAssistantMarkdown(text);
    } else {
      bubble.textContent = text;
    }

    row.appendChild(bubble);
    this.messagesTarget.appendChild(row);

    // A reader who is following the conversation sees a new answer from its
    // first line; one who scrolled up keeps their place and gets an offer.
    if (this.followLatest && role === "assistant" && !plain) {
      // A little headroom so the first line isn't flush against the edge.
      this.messagesTarget.scrollTop = Math.max(0, row.offsetTop - this.messagesTarget.offsetTop - 12);
    } else if (this.followLatest) {
      this.scrollDown();
    } else {
      this.jumpTarget.classList.remove("hidden");
    }
  }

  setBusy(busy) {
    this.busy = busy;
    if (busy) {
      const row = document.createElement("div");
      row.dataset.thinking = "1";
      row.className = "flex justify-start";
      row.innerHTML =
        '<div class="rounded-2xl rounded-bl-sm border border-line bg-ink-800 px-3 py-2 text-fg-muted">Kerri is getting started…</div>';
      this.messagesTarget.appendChild(row);
      if (this.followLatest) this.scrollDown();
    } else {
      this.messagesTarget.querySelector("[data-thinking]")?.remove();
    }
  }

  updateThinking(text) {
    const bubble = this.messagesTarget.querySelector("[data-thinking] > div");
    if (bubble && bubble.textContent !== text) {
      bubble.textContent = text;
      if (this.followLatest) this.scrollDown();
    }
  }

  scrollDown() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  trackScroll() {
    const el = this.messagesTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom < 40;
    this.followLatest = nearBottom;
    if (nearBottom) this.jumpTarget.classList.add("hidden");
  }

  jumpToLatest() {
    this.scrollDown();
    this.jumpTarget.classList.add("hidden");
    this.followLatest = true;
  }

  #persistHistory() {
    if (!this.storageKey) return;

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.history.slice(-50)));
    } catch (_e) {
      // Quota exceeded or unavailable; silently skip persistence.
    }
  }

  #rehydrateMessages() {
    this.history.forEach(({ role, content }) => {
      if (role === "user" || role === "assistant") {
        this.appendMessage(role, content);
      }
    });
    this.hidePrompts();
    this.scrollDown();
    this.followLatest = true;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  pageContext() {
    const main = document.querySelector("main");
    const heading = main?.querySelector("h1")?.innerText || document.querySelector("h1")?.innerText || "";
    const breadcrumb = document.querySelector("[data-shell-breadcrumb]")?.innerText || "";
    const mainText = (main?.innerText || "").replace(/\s+\n/g, "\n").trim();

    return {
      path: window.location.pathname,
      title: document.title,
      breadcrumb: breadcrumb.slice(0, 160),
      heading: heading.slice(0, 240),
      main_text: mainText.slice(0, 5000),
    };
  }
}
